/*
 * @description
 * Definisce la classe `User` e il relativo tipo `UserType` per rappresentare
 * e gestire le informazioni degli utenti nel sistema.
 *
 * La classe `User` include:
 * - Proprietà per l'ID utente, email, nome, cognome, tipo di utente e password.
 * - Un metodo per ottenere le iniziali del nome e del cognome.
 * - Un metodo per ottenere il nome completo dell'utente.
 * L'enumerazione `UserType` specifica i diversi ruoli che un utente può avere
 * nel sistema: 'admin', 'physician' e 'operator'.
 * */

import AnnotationCC from "../../atom/api/AnnotationCommunicationController";
import PhysicianTask from "../../atom/models/annotations/PhysicianTask";

export enum UserType {
    ADMIN = "admin",
    PHYSICIAN = "physician",
    OPERATOR = "operator"
}

export type AnnotationToolAccess = {
    id: number;
    name: string;
    endpoint?: string;
    access: boolean;
};

export type UserData = {
    id: number;
    email?: string;
    name?: string;
    surname?: string;
    type: UserType;
};

export const isUserEmailValid = (email?: string): boolean => {
    if (email === undefined) return false;
    email = email.trim();
    return /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);
};

export const isUserValid = (data: UserData): boolean => {
    return (
        data.name !== undefined &&
        data.name.trim() !== "" &&
        data.surname !== undefined &&
        data.surname.trim() !== "" &&
        data.email !== undefined &&
        isUserEmailValid(data.email)
    );
};

export type UserDataKey = keyof UserData;

export default class User {
    id: number;
    email: string;
    name: string;
    surname?: string;
    type: UserType;
    password?: string;

    toolsAccess: AnnotationToolAccess[] = [];
    private _toolsSynchronized: boolean = false;

    constructor(userData: UserData, password?: string) {
        this.id = userData.id;
        this.email = userData.email || "";
        this.name = userData.name || "";
        this.surname = userData.surname;
        this.type = userData.type;
        this.password = password;
    }

    //Restituisce le iniziali del nome e cognome dell'utente
    getInitials(): string {
        return this.surname ? this.name[0].toUpperCase() + this.surname[0].toUpperCase() : this.name[0].toUpperCase();
    }

    //Restituisce il nome completo dell'utente.

    fullName(): string {
        return this.surname ? `${this.name} ${this.surname}` : this.name;
    }

    filter = (search: string): boolean => {
        return this.fullName().toLowerCase().includes(search.toLowerCase());
    };

    fetchToolsAccess = async (): Promise<AnnotationToolAccess[]> => {
        if (!this._toolsSynchronized) {
            this.toolsAccess = await AnnotationCC.getUserAnnotationTool(this.id);
            this._toolsSynchronized = true;
        }
        return this.toolsAccess;
    };

    toggleAnnotationToolAccess = async (
        annotationToolId: number,
        access: boolean,
        endpoint?: string
    ): Promise<string> => {
        // Concedere/revocare sono ora due chiamate distinte lato server (v.
        // AnnotationCommunicationController.ts) - il chiamante continua a vedere
        // un solo metodo, la scelta tra le due resta un dettaglio interno.
        let instructions = "";
        if (access) {
            instructions = await AnnotationCC.grantUserAnnotationToolAccess(this.id, annotationToolId, endpoint ?? "");
        } else {
            await AnnotationCC.revokeUserAnnotationToolAccess(this.id, annotationToolId);
        }

        this.toolsAccess = this.toolsAccess.map(tool => {
            if (tool.id === annotationToolId) {
                tool.access = access;
                tool.endpoint = endpoint;
            }
            return tool;
        });

        return instructions;
    };

    tasks = (cnt: number = 20, offset: number = 0): Promise<PhysicianTask[]> => {
        return AnnotationCC.getPhysicianTasks(this.id, cnt, offset);
    };

    getData = (): UserData => {
        return {
            email: this.email,
            name: this.name,
            surname: this.surname,
            type: this.type,
            id: this.id
        };
    };
}
